import React, {FC, useEffect, useState} from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import {Link, useHistory} from 'react-router-dom';
import {Button, ButtonRow, ConfirmProps, SpinnerContainer, withConfirm} from '@deviceinsight/ng-ui-basic-components';
import {ContentHeader, messageBoxError, messageBoxSuccess} from '@deviceinsight/ng-ui-components';
import {Asset} from '@deviceinsight/ng-ui-api-client';

import RuleAsset from '../ruleengine/assetselection/RuleAsset';
import {MigrationToSave} from '../../modules/assetManagement/types';
import {createMigration} from '../../api/assetManagementApi';

import './CreateDeviceMigrationPage.less';

type Props = ConfirmProps;

const CreateDeviceMigrationPage: FC<Props> = ({confirm}) => {
	enum ValidationResults {
		VALID,
		INVALID_ASSETS_ARE_EQUAL,
		INVALID_ASSET_TYPES_INCOMPATIBLE
	}

	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const [validationResult, setValidationResult] = useState<ValidationResults>(ValidationResults.VALID);

	const [sourceAsset, setSourceAsset] = useState<Asset>();
	const [targetAsset, setTargetAsset] = useState<Asset>();

	const migrationListPath = `/portaladmin/assetmanagement`;

	const HEADER_TITLE = {
		id: 'assetmgmt.devicemigration.createTitle',
		defaultValue: 'Create Device Migration'
	};
	const sourceAssetLabel = {
		id: 'assetmgmt.devicemigration.sourceAssetLabel',
		defaultValue: 'Source Asset'
	};
	const targetAssetLabel = {
		id: 'assetmgmt.devicemigration.targetAssetLabel',
		defaultValue: 'Target Asset'
	};
	const saveButton = {
		id: 'assetmgmt.devicemigration.saveButton',
		defaultValue: 'Save'
	};
	const cancelButton = {
		id: 'assetmgmt.devicemigration.cancelButton',
		defaultValue: 'Cancel'
	};
	const CREATE_MIGRATION_CONFIRMATION_TITLE = {
		id: 'assetmgmt.devicemigration.createMigrationConfirmTitle',
		defaultValue: 'Create Device Migration'
	};
	const CREATE_MIGRATION_CONFIRMATION_BODY = {
		id: 'assetmgmt.devicemigration.createMigrationConfirmBody',
		defaultValue:
			'Attention: All datapoints on the Target Asset will be deleted. The Source Asset will be replaced by the Target Asset for rules, KPIs and dashboards. All data from the Source Asset will be migrated to the Target Asset. The Source Asset will remain in your Asset Group "My Devices". If necessary you can delete it separately.'
	};
	const assetsAreEqualValidationMsg = {
		id: 'assetmgmt.devicemigration.assetsAreEqualValidationMsg',
		defaultValue: 'Source and target asset must not be the same!'
	};
	const assetsAreIncompatibleValidationMsg = {
		id: 'assetmgmt.devicemigration.assetsAreIncompatibleValidationMsg',
		defaultValue: 'Source and target asset have a different asset type!'
	};

	const onSubmit = () => {
		const migrationToSave: MigrationToSave = {
			sourceAssetUrn: sourceAsset?.urn,
			targetAssetUrn: targetAsset?.urn
		};
		createDeviceMigration(migrationToSave);
	};

	const createDeviceMigration = (mig: MigrationToSave) => {
		if (sourceAsset && targetAsset) {
			confirm(CREATE_MIGRATION_CONFIRMATION_TITLE, CREATE_MIGRATION_CONFIRMATION_BODY).then(() => {
				setLoading(true);
				createMigration(mig)
					.catch(() =>
						messageBoxError(
							<Trans i18nKey="assetmgmt.devicemigration.couldntCreateMigrationMsg">
								Device Migration could not be created!
							</Trans>
						)
					)
					.then(() => {
						messageBoxSuccess(
							<Trans i18nKey="assetmgmt.devicemigration.migrationCreationSuccess">
								Device Migration was created
							</Trans>
						);
						history.push(migrationListPath);
					})
					.finally(() => setLoading(false));
			});
		} else {
			messageBoxError(
				<Trans i18nKey="assetmgmt.devicemigration.couldntCreateMigrationMsg">
					Device Migration could not be created!
				</Trans>
			);
		}
	};

	useEffect(() => {
		if (sourceAsset === undefined || targetAsset === undefined) {
			setValidationResult(ValidationResults.VALID);
		} else if (sourceAsset?.id === targetAsset?.id) {
			setValidationResult(ValidationResults.INVALID_ASSETS_ARE_EQUAL);
		} else if (sourceAsset?.properties.type !== targetAsset?.properties.type) {
			setValidationResult(ValidationResults.INVALID_ASSET_TYPES_INCOMPATIBLE);
		} else {
			setValidationResult(ValidationResults.VALID);
		}
	}, [
		ValidationResults.INVALID_ASSETS_ARE_EQUAL,
		ValidationResults.INVALID_ASSET_TYPES_INCOMPATIBLE,
		ValidationResults.VALID,
		sourceAsset,
		targetAsset
	]);

	return (
		<div className="device-migration-create">
			<h2>
				<ContentHeader title={HEADER_TITLE} parentPageLink subPagePathPrefixArray={['/create']}></ContentHeader>
			</h2>

			<SpinnerContainer show={loading}>
				<div className="label-row">
					<label className="asset-label">
						<Trans i18nKey={sourceAssetLabel.id}>{sourceAssetLabel.defaultValue}</Trans>
					</label>
				</div>
				<div className="asset-row">
					<div className="asset-picker-button">
						<RuleAsset
							assetGroup={undefined}
							onAssetSelect={(asset) => setSourceAsset(asset)}
							asset={undefined}
							canEdit={true}
							data-testid="sourceAsset-button"
						/>
					</div>
					<div className="asset-data">
						<Link
							to={`/assets/${sourceAsset?.id}/dashboard`}
							data-testid={`sourceAsset-${sourceAsset?.name}-name`}>
							{sourceAsset?.name}
						</Link>
					</div>
					<div className="asset-data" data-testid={`sourceAsset-${sourceAsset?.urn}-urn`}>
						{sourceAsset?.urn}
					</div>
				</div>

				<div className="label-row">
					<label className="asset-label">
						<Trans i18nKey={targetAssetLabel.id}>{targetAssetLabel.defaultValue}</Trans>
					</label>
				</div>
				<div className="asset-row">
					<div className="asset-picker-button">
						<RuleAsset
							assetGroup={undefined}
							onAssetSelect={(asset) => setTargetAsset(asset)}
							asset={undefined}
							canEdit={true}
							data-testid="targetAsset-button"
						/>
					</div>
					<div className="asset-data">
						<Link
							to={`/assets/${targetAsset?.id}/dashboard`}
							data-testid={`targetAsset-${targetAsset?.name}-name`}>
							{targetAsset?.name}
						</Link>
					</div>
					<div className="asset-data" data-testid={`targetAsset-${targetAsset?.urn}-urn`}>
						{targetAsset?.urn}
					</div>
				</div>

				{validationResult === ValidationResults.INVALID_ASSET_TYPES_INCOMPATIBLE ? (
					<label className="validation">
						<Trans i18nKey={assetsAreIncompatibleValidationMsg.id}>
							{assetsAreIncompatibleValidationMsg.defaultValue}
						</Trans>
					</label>
				) : undefined}
				{validationResult === ValidationResults.INVALID_ASSETS_ARE_EQUAL ? (
					<label className="validation">
						<Trans i18nKey={assetsAreEqualValidationMsg.id}>
							{assetsAreEqualValidationMsg.defaultValue}
						</Trans>
					</label>
				) : undefined}

				<ButtonRow>
					<Button
						data-testid="save-migration-button"
						primary
						disabled={
							loading ||
							!sourceAsset ||
							!targetAsset ||
							validationResult === ValidationResults.INVALID_ASSET_TYPES_INCOMPATIBLE ||
							validationResult === ValidationResults.INVALID_ASSETS_ARE_EQUAL
						}
						onClick={onSubmit}>
						<Trans i18nKey={saveButton.id}>{saveButton.defaultValue}</Trans>
					</Button>
					<Button
						data-testid="cancel-migration-button"
						onClick={() => history.push('/portaladmin/assetmanagement')}
						style={{
							marginRight: 0
						}}>
						<Trans i18nKey={cancelButton.id}>{cancelButton.defaultValue}</Trans>
					</Button>
				</ButtonRow>
			</SpinnerContainer>
		</div>
	);
};

export default withConfirm(CreateDeviceMigrationPage);
